import type {
  InfiniteData,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';
import type { Simplify, ValueOf } from 'type-fest';

import { isMultiPracticeUser as isMultiPracticeUserUtil } from '@/modules/login/login-utils';
import { directorPracticeIdAtom } from '@/utils/hooks/useDirectorPracticeView';

import {
  appointmentPatientsAtom,
  hasHuddleDataAtom,
  searchAllPracticesAtom,
  vaultAppointmentsAtom,
} from '../modules/patientList/atoms';

import { makeAPIRequestReactQuery } from './api';

const REFETCH_INTERVAL_IN_SECONDS = 30;

export interface GetAppointmentsResponse {
  data: Appointment[];
  lastSync?: string;
  nextCursor?: number;
  /** don't trust this yet. */
  total: number;
}

export const useAppointments = (
  args?: TAppointmentsArgs,
  options?: UseInfiniteQueryOptions<GetAppointmentsResponse, unknown>,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector((state: any) => state.login.user);
  const setHasHuddleData = useSetAtom(hasHuddleDataAtom);
  const isVaultSearchEnabled = useAtomValue(vaultAppointmentsAtom);
  const isOrgSearchEnabled = useAtomValue(searchAllPracticesAtom);
  const setAppointmentPatients = useSetAtom(appointmentPatientsAtom);
  const directorPracticeId = useAtomValue(directorPracticeIdAtom);
  const user = useSelector((state: any) => state.login.user);
  const isMultiPracticeUser = isMultiPracticeUserUtil(user);

  const practiceId = args?.appointmentOpts?.practiceId || currentPractice;

  // For multi-practice users, Redux initializes primaryPracticeId to the first
  // practice from their practices list (returned from users endpoint). When the
  // user selects another practice, an initial appointments call fires with the
  // default primaryPracticeId before Redux updates, which causes a 403 error
  // and forced logout if that practice is inactive. To prevent this, skip the
  // call until the Redux primaryPracticeId matches the selected
  // directorPracticeId. Single-practice and director users are unaffected.
  const isPrimaryPracticeIdUpdated = isMultiPracticeUser
    ? practiceId === directorPracticeId
    : true;

  const allArgs = {
    ...args,
    appointmentOpts: {
      ...args?.appointmentOpts,
      vaultSearchEnabled: !args?.patientOpts?.name && isVaultSearchEnabled,
      orgSearchEnabled: !!args?.patientOpts?.name && isOrgSearchEnabled,
    },
  };

  const handleSuccess = ({ pages }: InfiniteData<GetAppointmentsResponse>) => {
    setHasHuddleData(!!pages[pages.length - 1].lastSync);

    const flattened = pages
      .map(({ data: pageData }) => pageData.map(({ patient }) => patient))
      .flat();
    setAppointmentPatients(flattened);
  };

  return useInfiniteQuery(
    ['appointments', allArgs, practiceId],
    ({ pageParam }) =>
      makeAPIRequestReactQuery<GetAppointmentsResponse>(
        `${practiceId}/appointments`,
        {
          ...allArgs,
          meta: {
            ...allArgs?.meta,
            pagination: {
              cursor: pageParam,
              limit: allArgs?.meta?.pagination?.limit,
            },
          },
        },
        'get',
        { headers: { practice: practiceId, org: currentOrg } },
      ),
    {
      getNextPageParam: (lastPage) => {
        return lastPage?.nextCursor;
      },
      /** @ts-expect-error idk */
      refetchInterval: REFETCH_INTERVAL_IN_SECONDS * 1000,
      enabled:
        Boolean(practiceId) &&
        Boolean(currentOrg) &&
        isPrimaryPracticeIdUpdated,
      onSuccess: handleSuccess,
      ...options,
    },
  );
};

export const useAppointmentsByVisitId = (
  visitId: number,
  practiceId: string,
) => {
  const { primaryOrganizationId: currentOrg } = useSelector(
    (state: any) => state.login.user,
  );

  const path = `${practiceId}/appointments/visit/${visitId}`;

  return useQuery([path], () =>
    makeAPIRequestReactQuery<AppointmentData>(path, {}, 'get', {
      headers: {
        Org: currentOrg,
      },
    }),
  );
};

const TreatmentStatus = {
  completed: 'COMPLETED',
  planned: 'PLANNED',
  scheduled: 'SCHEDULED',
  other: 'OTHER',
} as const;
export type TTreatmentStatus = ValueOf<typeof TreatmentStatus>;

const TreatmentCategory = {
  ADJUNCTIVE_GENERAL_SERVICES: 'ADJUNCTIVE GENERAL SERVICES',
  DIAGNOSTIC: 'DIAGNOSTIC',
  ENDODONTICS: 'ENDODONTICS',
  IMPLANT_SERVICES: 'IMPLANT SERVICES',
  MAXILLOFACIAL_PROSTHETICS: 'MAXILLOFACIAL PROSTHETICS',
  ORAL_MAXILLOFACIAL_SURGERY: 'ORAL & MAXILLOFACIAL SURGERY',
  ORTHODONTICS: 'ORTHODONTICS',
  OTHER: 'OTHER',
  PERIODONTICS: 'PERIODONTICS',
  PREVENTITIVE: 'PREVENTITIVE',
  PROSTHODONTICS_REMOVABLE: 'PROSTHODONTICS, REMOVABLE',
  PROSTHODONTICS_SERVICES: 'PROSTHODONTICS, SERVICES',
  RESTORATIVE: 'RESTORATIVE',
} as const;
export type TTreatmentCategory = Simplify<ValueOf<typeof TreatmentCategory>>;

export const ToothQuadrants = {
  QUADRANT_1: '1-8',
  QUADRANT_2: '9-16',
  QUADRANT_3: '17-24',
  QUADRANT_4: '25-32',
} as const;
export type TToothQuadrants = Simplify<
  ValueOf<typeof ToothQuadrants> | 'Unknown'
>;

export type TAppointmentsArgs = {
  /**
   * Controls what unifiedAppointment we select
   * exceptions being, onlyImaging and onlyPms.
   * which filter on the estimatedVisit and pmsAppointment
   * but that trickles up to the unifiedAppointment.
   */
  appointmentOpts?: {
    /**
     * Start value of the appointments we care about.
     * UnifiedAppointments['appointmentOn']
     */
    startDate?: Date | undefined;
    /**
     * End value of the appointments we care about.
     * UnifiedAppointments['appointmentOn']
     */
    endDate?: Date | undefined;
    /**
     * Only include appointments with analyses
     * Required: ImagingPatients, EstimatedVisits
     */
    onlyImaging?: boolean | undefined;
    /**
     * Only include appointments with Pms Data
     * Required: PmsPatients, PmsAppointments
     */
    onlyPms?: boolean | undefined;
    /**
     * Select appointments with Operatories['displayName'] ilike
     */
    operatories?: string | undefined;

    /**
     * Start value of the estimated visits (imaging events)
     * imagingStartDate <= EstimatedVisit['imageCreatedAt']
     */
    imagingStartDate?: Date | undefined;
    /**
     * End value of the estimated visits (imaging events)
     * imagingEndDate >= EstimatedVisit['imageCreatedAt']
     */
    imagingEndDate?: Date | undefined;
    /**
     * This controls a workaround for practices that are using both DDAI and standalone (I think?)
     */
    selectedView?: string | undefined;
    /**
     * vaultSearchEnabled
     *
     * handles whether we grab appointments from the practice's organization's vault practice rather
     * than the given practice.
     *
     * this is done to support walkins in configurations like dentrix enterprise where appts can land in a "vault"
     * practice
     */
    vaultSearchEnabled?: boolean;
    /**
     * orgSearchEnabled
     *
     * handles whether we switch the search to go through the practice's organization's practices rather
     * than the given practice.
     *
     * this allows searching for a patient that appeared in another practice on the organization
     */
    orgSearchEnabled?: boolean;
    practiceId?: string;
  };
  patientOpts?: {
    /**
     * Select appointments with
     * PmsPatient['displayName'] iLike OR
     * ImagingPatient['name'] iLike
     */
    name?: string;
  };
  treatmentOpts?: {
    /**
     * Select appointments with treatment data
     * Required: PmsTreatments, PmsPatients
     */
    onlyTreatments?: boolean;
    /**
     * Select appointments with treatments matching category
     * category === TTreatmentCategory
     */
    category?: TTreatmentCategory;
  };
  referrals?: {
    /**
     * Filter appointments by referral status
     * - 'submitted': Only appointments with submitted referrals
     * - 'needs_referral': Only appointments with referrals that need to be submitted
     * - 'failed': Only appointments with failed referrals that need resubmission
     * - 'both': Appointments with either submitted or needs_referral status
     */
    status?: 'submitted' | 'needs_referral' | 'failed' | 'both';
  };
  meta?: TMeta<{ id: number; appointmentOn: Date }>;
};

export interface AppointmentPatient {
  dob?: string;
  firstName?: string;
  imagingPatientId?: string;
  imagingPatientVideaId?: string;
  sourcePatientId?: string | null; // Currently leveraged only for standalone patient visits
  isNewPatient?: boolean;
  lastName?: string;
  pmsPatientId?: string;
  pmsPatientVideaId?: string;
  videaId?: string;
}

export interface AppointmentProvider {
  id?: string;
  /**
   * PmsProviders['firstName']
   */
  firstName?: string | undefined | null;
  /**
   * PmsProviders['lastName']
   */
  lastName?: string | undefined | null;
}

export interface ReferralData {
  id: string;
  status: 'submitted' | 'needs_referral' | 'failed';
  referralReason: string;
  patientFirstName: string;
  patientLastName: string;
  patientEmail: string;
  patientPhone: string;
  toothNumbers: string[];
  imageUrls: string[];
  narrative: string;
  currentStep: string;
  createdAt: string;
  updatedAt: string;
}

export type AppointmentData = {
  patient: AppointmentPatient;
  appointment: {
    /**
     * UnifiedAppointments['id']
     */
    unifiedId: string;
    /**
     * UnifiedAppointments['appointmentOn']
     */
    start: string;
    /**
     * UnifiedAppointments['practiceId'] weighed against the organization.vaultPracticeVideaId || the practice.id
     *
     * indicates whether this is a walkin or not. if it's true, then walkin else it's associated directly with the
     * practice
     */
    isInVault?: boolean;
    /**
     * PmsAppointments['endDate']
     */
    end?: string | undefined | null;
    /**
     * EstimatedVisit['imageCreatedAt']
     */
    imagesAt?: string | undefined | null;
    /**
     * PK
     */
    estimatedVisitId?: number | undefined | null;
    /**
     * PmsAppointments['videaId']
     */
    pmsAppointmentId?: string | undefined | null;
    /** only going to get these back in a dentrix enterprise name search */
    practiceName?: string | undefined;
    practiceVideaId?: string | undefined;
    operatory?: {
      id?: string;
      /**
       * PmsOperatories['displayName']
       * ex: Dr. Dudebro
       */
      name?: string | undefined | null;
      /**
       * PmsOperatories['shortName']
       * ex: OP-4
       */
      shortName?: string | undefined | null;
    };
    provider?: AppointmentProvider;
    /**
     * Referral information - only present when referrals parameter is used
     */
    referral?: ReferralData | null;
  };
};

export type Appointment = AppointmentData & {
  /**
   * A UserEvent with 'eventName'='patient.view'
   *
   * exists for:
   *    this user
   *    this visit
   *    this number of images (dedupe mechanism)
   */
  viewed?: boolean;
  lastTreatments: {
    /**
     * Date of their last Treatment with
     * a BW code.
     */
    lastBw?: Date | undefined | null;
    /**
     * Date of their last Treatment with
     * a Fluoride code.
     */
    lastFluoride?: Date | undefined | null;
    /**
     * Date of their last Treatment with
     * a FMX code.
     */
    lastFmx?: Date | undefined | null;
    /**
     * Date of their last Treatment with
     * a perio chart code.
     */
    lastPerioChart?: Date | undefined | null;
    /**
     * Date of their last Treatment with
     * a perio maintenance code.
     */
    lastPerioMaintenance?: Date | undefined | null;
    /**
     * Date of their last Treatment with
     * a prophy code.
     */
    lastProphy?: Date | undefined | null;
    /**
     * Date of their last Treatment with
     * a srp code.
     */
    lastSrp?: Date | undefined | null;
  };
  /**
   * {@link https://videahealth.atlassian.net/wiki/spaces/SOFTWARE/pages/2600632340/Huddle+Individual+Patient+Data+-+Alerts+Actions confluence doc}
   */
  tasks?: {
    isPerioPatient: boolean;
    isPerioMaintenanceDue: boolean;
    isPerioChartDue: boolean;
    isFmxDue: boolean;
    isBwDue: boolean;
    isProphyDue: boolean;
    isFluorideDue: boolean;
  };

  claims?: {
    needsReview?: boolean;
  };
};

export type TTreatment = {
  /**
   * {@link TreatmentCategory}
   */
  category: TTreatmentCategory;
  cdtCode?: string;
  shortName?: string;
  toothNum?: string;
  toothRange?: string;
  treatmentDate?: Date | string;
  directTreatmentDate?: Date | string;
  latestAppointmentStartDate?: Date | string;
  pmsTreatmentId?: number;
  type?: string;
  /**
   * {@link TreatmentStatus}
   */
  status: TTreatmentStatus;
  treatmentId: string;
  _dontUseOnlyForRCMQuadrant: string | null;
};
export type TTreatments = {
  [Property in TTreatmentStatus]?: TTreatment[];
};

export type TMeta<
  T extends Record<string, unknown> | number | string | undefined,
> = Simplify<{
  pagination?: {
    cursor?: T extends Record<string, unknown>
      ? T extends { id: unknown }
        ?
            | T['id']
            | [
                [keyof T, unknown, 'ASC' | 'DESC'],
                ['id', 'ASC' | 'DESC', T['id']],
              ]
        : [[keyof T, unknown, 'ASC' | 'DESC']]
      : number | string | undefined;
    limit?: number;
    offset?: number;
  };
  sort?: {
    [Property in keyof T]?: 'ASC' | 'DESC';
  };
}>;
